import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { getTutorApiKeyStatus } from '@/lib/profile';
import {
  buildExerciseProgress,
  type AttemptRow,
  type ExerciseProgress,
} from '@/lib/tutor/student-progress';

/** Longueur minimale d'une requête pour déclencher la recherche d'exercices. */
export const EXERCISE_SEARCH_MIN_LEN = 2;

// ── Types ──────────────────────────────────────────────────────────────────────

export type ClassSummary = {
  id: string;
  name: string;
  description: string | null;
  level: SchoolLevel | null;
  subject: SubjectSlug | null;
  invite_code: string;
  archived_at: string | null;
  member_count: number;
  created_at: string;
};

export type ClassMember = {
  member_id: string;
  student_id: string;
  display_name: string;
  level: SchoolLevel | null;
  xp: number;
  streak_days: number;
  /** Dernière activité (repère « décroche » sur la fiche classe). */
  last_activity_at: string | null;
  joined_at: string;
};

export type ClassDetail = ClassSummary & { members: ClassMember[] };

export type TutorLinkedStudent = {
  link_id: string;
  status: 'pending' | 'active' | 'revoked';
  initiated_by: 'tutor' | 'student';
  student_id: string;
  display_name: string;
  level: SchoolLevel | null;
  xp: number;
  streak_days: number;
  last_activity_at: string | null;
  invited_at: string;
  accepted_at: string | null;
  /** Matière(s) couvertes par ce lien (migration 0227) — vide = toutes matières. */
  subjects: SubjectSlug[];
  /**
   * Statut alternant du PROFIL (migration 0237) : un alternant déclaré et lié
   * mais sans tuteur académique (`academic_tutor_id` null) est « à rattacher »
   * — il ne doit pas rester noyé dans les élèves.
   */
  is_alternant: boolean;
  academic_tutor_id: string | null;
};

export type MyExerciseSummary = {
  id: string;
  title: string;
  status: 'draft' | 'published' | 'archived' | 'awaiting_review';
  difficulty: 'easy' | 'medium' | 'hard';
  estimated_minutes: number | null;
  created_at: string;
  chapter_id: string | null;
  chapter_title: string;
  subject: SubjectSlug | null;
  level: SchoolLevel | null;
  step_count: number;
  /** true = exercice fondamental (banque de questions Warmup, accès gratuit). */
  is_fundamental: boolean;
  /** true = exercice diagnostic (QCM de cours, accès gratuit, objectif Diagnostic). */
  is_diagnostic: boolean;
  /** Illustration de l'exercice (générée à la publication) — null si absente. */
  illustration_url: string | null;
  /** Auteur de l'exercice (avatar affiché sur la carte). Null pour les exos système. */
  author: { display_name: string | null; avatar_url: string | null } | null;
};

export type TutorDashboardStats = {
  total_students: number;
  active_classes: number;
  total_exercises: number;
  students_at_risk: number;
};

export type RecentActivity = {
  attempt_id: string;
  student_name: string;
  exercise_title: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  started_at: string;
};

// ── Queries ───────────────────────────────────────────────────────────────────

export function useTutorClasses(tutorId: string) {
  return useQuery({
    queryKey: ['tutor-classes', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<ClassSummary[]> => {
      const { data: classes, error } = await supabase
        .from('classes')
        .select('id, name, description, level, subject, invite_code, archived_at, created_at')
        .eq('tutor_id', tutorId)
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message);
      if (!classes || classes.length === 0) return [];

      const classIds = classes.map((c) => c.id);
      const { data: memberCounts } = await supabase
        .from('class_members')
        .select('class_id')
        .in('class_id', classIds)
        .eq('status', 'active');

      const countByClass = new Map<string, number>();
      for (const row of memberCounts ?? []) {
        countByClass.set(row.class_id, (countByClass.get(row.class_id) ?? 0) + 1);
      }

      return classes.map((c) => ({
        id: c.id,
        name: c.name,
        description: c.description,
        level: c.level as SchoolLevel | null,
        subject: c.subject as SubjectSlug | null,
        invite_code: c.invite_code,
        archived_at: c.archived_at,
        member_count: countByClass.get(c.id) ?? 0,
        created_at: c.created_at,
      }));
    },
  });
}

export function useTutorClassDetail(classId: string) {
  return useQuery({
    queryKey: ['tutor-class-detail', classId],
    enabled: !!classId,
    queryFn: async (): Promise<ClassDetail | null> => {
      const { data: cls, error } = await supabase
        .from('classes')
        .select('id, name, description, level, subject, invite_code, archived_at, created_at')
        .eq('id', classId)
        .maybeSingle();

      if (error) throw new Error(error.message);
      if (!cls) return null;

      const { data: rawMembers } = await supabase
        .from('class_members')
        .select('id, student_id, joined_at')
        .eq('class_id', classId)
        .eq('status', 'active')
        .order('joined_at', { ascending: true });

      const members_data = rawMembers ?? [];
      const studentIds = members_data.map((m) => m.student_id);
      let profileById = new Map<string, { display_name: string }>();
      let studentProfileById = new Map<
        string,
        {
          level: SchoolLevel | null;
          xp: number;
          streak_days: number;
          last_activity_at: string | null;
        }
      >();

      if (studentIds.length > 0) {
        const [{ data: profiles }, { data: sps }] = await Promise.all([
          supabase.from('profiles').select('id, display_name:visible_name').in('id', studentIds),
          supabase
            .from('student_profiles')
            .select('id, level, xp, streak_days, last_activity_at')
            .in('id', studentIds),
        ]);
        profileById = new Map((profiles ?? []).map((p) => [p.id, p]));
        studentProfileById = new Map((sps ?? []).map((sp) => [sp.id, sp]));
      }

      const members: ClassMember[] = members_data.map((m) => {
        const p = profileById.get(m.student_id);
        const sp = studentProfileById.get(m.student_id);
        return {
          member_id: m.id,
          student_id: m.student_id,
          display_name: p?.display_name ?? '(profil inaccessible)',
          level: (sp?.level ?? null) as SchoolLevel | null,
          xp: sp?.xp ?? 0,
          streak_days: sp?.streak_days ?? 0,
          last_activity_at: sp?.last_activity_at ?? null,
          joined_at: m.joined_at,
        };
      });

      return {
        id: cls.id,
        name: cls.name,
        description: cls.description,
        level: cls.level as SchoolLevel | null,
        subject: cls.subject as SubjectSlug | null,
        invite_code: cls.invite_code,
        archived_at: cls.archived_at,
        member_count: members.length,
        created_at: cls.created_at,
        members,
      };
    },
  });
}

export function useTutorStudents(tutorId: string) {
  return useQuery({
    queryKey: ['tutor-students', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<TutorLinkedStudent[]> => {
      const { data: links, error } = await supabase
        .from('tutor_links')
        .select('id, status, initiated_by, student_id, invited_at, accepted_at, subjects')
        .eq('tutor_id', tutorId)
        .order('invited_at', { ascending: false });

      if (error) throw new Error(error.message);
      if (!links || links.length === 0) return [];

      const studentIds = links.map((l) => l.student_id);
      // is_alternant/academic_tutor_id (migration 0237) sont postérieurs aux types
      // générés (stale, cf. CLAUDE.md) → client générique pour ce select.
      type StudentProfileRow = {
        id: string;
        level: SchoolLevel | null;
        xp: number | null;
        streak_days: number | null;
        last_activity_at: string | null;
        is_alternant: boolean | null;
        academic_tutor_id: string | null;
      };
      const [{ data: profiles }, { data: studentProfiles }] = await Promise.all([
        supabase.from('profiles').select('id, display_name:visible_name').in('id', studentIds),
        (supabase as unknown as SupabaseClient)
          .from('student_profiles')
          .select('id, level, xp, streak_days, last_activity_at, is_alternant, academic_tutor_id')
          .in('id', studentIds),
      ]);

      const profileById = new Map((profiles ?? []).map((p) => [p.id, p]));
      const spById = new Map(
        ((studentProfiles ?? []) as StudentProfileRow[]).map((sp) => [sp.id, sp]),
      );

      return links.map((l) => {
        const p = profileById.get(l.student_id);
        const sp = spById.get(l.student_id);
        return {
          link_id: l.id,
          status: l.status as TutorLinkedStudent['status'],
          initiated_by: l.initiated_by as TutorLinkedStudent['initiated_by'],
          student_id: l.student_id,
          display_name: p?.display_name ?? '(profil inaccessible)',
          level: (sp?.level ?? null) as SchoolLevel | null,
          xp: sp?.xp ?? 0,
          streak_days: sp?.streak_days ?? 0,
          last_activity_at: sp?.last_activity_at ?? null,
          invited_at: l.invited_at,
          accepted_at: l.accepted_at,
          subjects: (l.subjects ?? []) as SubjectSlug[],
          is_alternant: Boolean(sp?.is_alternant),
          academic_tutor_id: sp?.academic_tutor_id ?? null,
        };
      });
    },
  });
}

export type TutorStudentDetail = {
  student_id: string;
  display_name: string;
  avatar_url: string | null;
  level: SchoolLevel | null;
  xp: number;
  streak_days: number;
  last_activity_at: string | null;
  /** `none` = aucun lien individuel (ex. élève vu via une classe) : ses résultats ne sont pas lisibles. */
  link_status: 'pending' | 'active' | 'revoked' | 'none';
  invited_at: string | null;
  /** Mentorat académique alternance (migration 0237) — relation SÉPARÉE de tutor_links. */
  is_alternant: boolean;
  /** true = alternant DÉCLARÉ par l'étudiant ; false = basculé par son tuteur (migration 0250). */
  is_higher_ed_student: boolean;
  academic_tutor_id: string | null;
  stats: { total_attempts: number; completed_attempts: number; success_rate: number };
  recent_attempts: Array<{
    attempt_id: string;
    exercise_title: string;
    status: 'in_progress' | 'completed' | 'abandoned';
    started_at: string;
    completed_at: string | null;
  }>;
  active_assignments: Array<{
    id: string;
    exercise_title: string;
    status: 'pending' | 'in_progress';
    due_at: string | null;
    note: string | null;
    assigned_at: string;
  }>;
  completed_assignments: Array<{
    id: string;
    exercise_title: string;
    completed_at: string | null;
    assigned_at: string;
  }>;
};

export function useTutorStudentDetail(studentId: string, tutorId: string) {
  return useQuery({
    queryKey: ['tutor-student-detail', studentId, tutorId],
    enabled: !!studentId && !!tutorId,
    queryFn: async (): Promise<TutorStudentDetail> => {
      const [
        { data: profile },
        { data: sp },
        { data: link },
        { data: rawAttempts },
        { data: rawAssignments },
      ] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, display_name:visible_name, avatar_url')
          .eq('id', studentId)
          .maybeSingle(),
        // is_alternant/academic_tutor_id (migration 0237) sont postérieurs aux
        // types générés (stale, cf. CLAUDE.md) → client générique pour ce select.
        (supabase as unknown as SupabaseClient)
          .from('student_profiles')
          .select(
            'id, level, xp, streak_days, last_activity_at, is_alternant, is_higher_ed_student, academic_tutor_id',
          )
          .eq('id', studentId)
          .maybeSingle(),
        supabase
          .from('tutor_links')
          .select('status, invited_at')
          .eq('tutor_id', tutorId)
          .eq('student_id', studentId)
          .maybeSingle(),
        supabase
          .from('attempts')
          .select('id, status, started_at, completed_at, exercises(title)')
          .eq('student_id', studentId)
          .order('started_at', { ascending: false })
          .limit(20),
        supabase
          .from('assignments')
          .select('id, status, due_at, note, assigned_at, completed_at, exercises(title)')
          .eq('student_id', studentId)
          .order('assigned_at', { ascending: false }),
      ]);

      const attempts = (rawAttempts ?? []) as any[];
      const assignments = (rawAssignments ?? []) as any[];
      const completed = attempts.filter((a) => a.status === 'completed');

      return {
        student_id: studentId,
        display_name: profile?.display_name ?? '(profil inaccessible)',
        avatar_url: profile?.avatar_url ?? null,
        level: (sp?.level ?? null) as SchoolLevel | null,
        xp: sp?.xp ?? 0,
        streak_days: sp?.streak_days ?? 0,
        last_activity_at: sp?.last_activity_at ?? null,
        link_status: (link?.status ?? 'none') as TutorStudentDetail['link_status'],
        invited_at: link?.invited_at ?? null,
        is_alternant: Boolean((sp as { is_alternant?: boolean } | null)?.is_alternant),
        is_higher_ed_student: Boolean(
          (sp as { is_higher_ed_student?: boolean } | null)?.is_higher_ed_student,
        ),
        academic_tutor_id:
          (sp as { academic_tutor_id?: string | null } | null)?.academic_tutor_id ?? null,
        stats: {
          total_attempts: attempts.length,
          completed_attempts: completed.length,
          success_rate:
            attempts.length > 0 ? Math.round((completed.length / attempts.length) * 100) : 0,
        },
        recent_attempts: attempts.slice(0, 5).map((a) => ({
          attempt_id: a.id,
          exercise_title: a.exercises?.title ?? '(exercice supprimé)',
          status: a.status as 'in_progress' | 'completed' | 'abandoned',
          started_at: a.started_at,
          completed_at: a.completed_at,
        })),
        active_assignments: assignments
          .filter((a) => a.status !== 'completed')
          .map((a) => ({
            id: a.id,
            exercise_title: a.exercises?.title ?? '(exercice supprimé)',
            status: a.status as 'pending' | 'in_progress',
            due_at: a.due_at,
            note: a.note,
            assigned_at: a.assigned_at,
          })),
        completed_assignments: assignments
          .filter((a) => a.status === 'completed')
          .slice(0, 5)
          .map((a) => ({
            id: a.id,
            exercise_title: a.exercises?.title ?? '(exercice supprimé)',
            completed_at: a.completed_at,
            assigned_at: a.assigned_at,
          })),
      };
    },
  });
}

/**
 * Progression d'un élève sur les exercices qu'il refait souvent (≥ 2 tentatives
 * terminées) — alimente le graphe de tendance côté prof. Même portée d'accès que
 * `useTutorStudentDetail` (le prof lit les tentatives de son élève lié).
 */
export function useStudentExerciseProgress(studentId: string, tutorId: string) {
  return useQuery({
    queryKey: ['tutor-student-progress', studentId, tutorId],
    enabled: !!studentId && !!tutorId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ExerciseProgress[]> => {
      const { data } = await supabase
        .from('attempts')
        .select('id, exercise_id, score, status, started_at, completed_at, exercises(title)')
        .eq('student_id', studentId)
        .eq('status', 'completed')
        .eq('session_type', 'exercise')
        .not('exercise_id', 'is', null)
        .order('completed_at', { ascending: true })
        .limit(300);
      const rows: AttemptRow[] = (data ?? []).map((a: any) => ({
        id: a.id,
        exercise_id: a.exercise_id,
        score: a.score,
        status: a.status,
        started_at: a.started_at,
        completed_at: a.completed_at,
        exercise_title: a.exercises?.title ?? null,
      }));
      return buildExerciseProgress(rows);
    },
  });
}

/** Colonnes projetées pour un `MyExerciseSummary` (partagé par plusieurs queries). */
const EXERCISE_SUMMARY_SELECT =
  'id, title, status, difficulty, estimated_minutes, created_at, chapter_id, is_fundamental, is_diagnostic, illustration_url, chapters(title, subject, level), question_count:exercise_question_count, profiles!exercises_created_by_fkey(display_name:visible_name, avatar_url)';

/** Mappe une ligne `exercises` (jointe) vers un `MyExerciseSummary`. */
function mapExerciseSummaryRow(e: any): MyExerciseSummary {
  return {
    id: e.id,
    title: e.title,
    status: e.status,
    difficulty: e.difficulty,
    estimated_minutes: e.estimated_minutes,
    created_at: e.created_at,
    chapter_id: e.chapter_id ?? null,
    chapter_title: e.chapters?.title ?? '(chapitre inconnu)',
    subject: (e.chapters?.subject ?? null) as SubjectSlug | null,
    level: (e.chapters?.level ?? null) as SchoolLevel | null,
    // Questions V2 (colonne calculée `exercise_question_count`, migration 0296).
    step_count: e.question_count ?? 0,
    is_fundamental: !!e.is_fundamental,
    is_diagnostic: !!e.is_diagnostic,
    illustration_url: e.illustration_url ?? null,
    author: e.profiles
      ? {
          display_name: e.profiles.display_name ?? null,
          avatar_url: e.profiles.avatar_url ?? null,
        }
      : null,
  };
}

export function useMyExercises(tutorId: string) {
  return useQuery({
    queryKey: ['tutor-exercises', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<MyExerciseSummary[]> => {
      const { data, error } = await supabase
        .from('exercises')
        .select(EXERCISE_SUMMARY_SELECT)
        .eq('created_by', tutorId)
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message);

      return (data ?? []).map(mapExerciseSummaryRow);
    },
  });
}

/** Statut d'un défi Live School (miroir de l'enum `live_challenges.status`). */
export type LiveChallengeStatus = 'scheduled' | 'live' | 'finished' | 'snapshotted';

/** Un exercice rattaché à un défi Live School + la diffusion correspondante. */
export type LiveSchoolExercise = MyExerciseSummary & {
  live: {
    challenge_id: string;
    /** Instant de diffusion du défi (ISO UTC). */
    starts_at: string;
    status: LiveChallengeStatus;
    /** Niveau ciblé par le défi (peut différer si la source est réutilisée). */
    challenge_level: SchoolLevel;
    subject: SubjectSlug | null;
    title: string | null;
  };
};

/**
 * Exercices utilisés comme source d'un défi Live School (`live_challenges.
 * source_exercise_id`), avec la date/heure de diffusion. Une ligne PAR défi
 * (un même exercice diffusé plusieurs fois apparaît plusieurs fois).
 *
 * ⚠️ RLS `live_challenges` : un défi encore `scheduled` (à venir) n'est lisible
 * QUE par un admin (`status <> 'scheduled' or is_admin()`, migration 0156) →
 * réservé à l'admin côté UI. Les tables `live_*` sont postérieures aux types
 * stale (cf. CLAUDE.md) → client générique pour la lecture des défis.
 */
export function useLiveSchoolExercises(enabled: boolean) {
  return useQuery({
    queryKey: ['live-school-exercises'],
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<LiveSchoolExercise[]> => {
      const db = supabase as unknown as SupabaseClient;
      const { data: challenges, error } = await db
        .from('live_challenges')
        .select('id, level, subject, starts_at, status, title, source_exercise_id')
        .not('source_exercise_id', 'is', null)
        .order('starts_at', { ascending: false });
      if (error) throw new Error(error.message);

      const rows = (challenges ?? []) as Array<{
        id: string;
        level: SchoolLevel;
        subject: SubjectSlug | null;
        starts_at: string;
        status: LiveChallengeStatus;
        title: string | null;
        source_exercise_id: string;
      }>;
      if (rows.length === 0) return [];

      const ids = [...new Set(rows.map((r) => r.source_exercise_id))];
      const { data: exData, error: exErr } = await supabase
        .from('exercises')
        .select(EXERCISE_SUMMARY_SELECT)
        .in('id', ids);
      if (exErr) throw new Error(exErr.message);

      const byId = new Map<string, MyExerciseSummary>();
      for (const e of (exData ?? []) as any[]) byId.set(e.id, mapExerciseSummaryRow(e));

      const out: LiveSchoolExercise[] = [];
      for (const r of rows) {
        const ex = byId.get(r.source_exercise_id);
        if (!ex) continue; // exercice non lisible (non publié / autre auteur)
        out.push({
          ...ex,
          live: {
            challenge_id: r.id,
            starts_at: r.starts_at,
            status: r.status,
            challenge_level: r.level,
            subject: r.subject,
            title: r.title,
          },
        });
      }
      return out;
    },
  });
}

/**
 * Recherche des exercices (titre + énoncé + contenu V1/V2) TOUS AUTEURS
 * confondus — via la RPC security-definer `search_exercises` (migration 0181),
 * qui rejoue la RLS `read_published_or_owner` (publiés OR propres OR admin).
 * La RPC est postérieure aux types générés (stale, cf. CLAUDE.md) → client
 * générique (cast), comme apps/app/src/lib/parent-link.ts.
 */
export function useSearchExercises(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['search-exercises', trimmed],
    enabled: trimmed.length >= EXERCISE_SEARCH_MIN_LEN,
    staleTime: 30_000,
    queryFn: async (): Promise<MyExerciseSummary[]> => {
      const db = supabase as unknown as SupabaseClient;
      const { data, error } = await db.rpc('search_exercises', {
        p_query: trimmed,
        p_limit: 60,
      });
      if (error) throw new Error(error.message);
      return ((data ?? []) as any[]).map((r) => ({
        id: r.id,
        title: r.title,
        status: r.status,
        difficulty: r.difficulty,
        estimated_minutes: r.estimated_minutes ?? null,
        created_at: r.created_at,
        chapter_id: r.chapter_id ?? null,
        chapter_title: r.chapter_title ?? '(chapitre inconnu)',
        subject: (r.subject ?? null) as SubjectSlug | null,
        level: (r.level ?? null) as SchoolLevel | null,
        step_count: r.step_count ?? 0,
        is_fundamental: !!r.is_fundamental,
        is_diagnostic: !!r.is_diagnostic,
        illustration_url: r.illustration_url ?? null,
        author: r.author_name
          ? {
              display_name: r.author_name,
              avatar_url: r.author_avatar_url ?? null,
            }
          : null,
      }));
    },
  });
}

export type StudentSearchResult = {
  id: string;
  display_name: string;
  avatar_url: string | null;
  level: SchoolLevel | null;
};

export function useSearchStudentsByName(pattern: string) {
  return useQuery({
    queryKey: ['search-students-by-name', pattern],
    enabled: pattern.trim().length >= 2,
    staleTime: 30_000,
    queryFn: async (): Promise<StudentSearchResult[]> => {
      const { data, error } = await supabase.rpc('search_students_by_name', {
        p_name_pattern: pattern.trim(),
      });
      if (error) throw new Error(error.message);
      return (data ?? []).map((r: any) => ({
        id: r.id,
        display_name: r.display_name,
        avatar_url: r.avatar_url ?? null,
        level: (r.level ?? null) as SchoolLevel | null,
      }));
    },
  });
}

/**
 * Statut de la clé Anthropic du prof (has_key + last4) — via la RPC dédiée.
 * ⚠️ Ne lit JAMAIS la clé brute : `tutor_profiles.anthropic_api_key` est
 * write-only côté client (REVOKE SELECT colonne, migration 0190).
 */
export function useTutorApiKeyStatus(userId: string) {
  return useQuery({
    queryKey: ['tutor-api-key-status', userId],
    enabled: !!userId,
    queryFn: () => getTutorApiKeyStatus(supabase),
  });
}

export function useTutorDashboardStats(tutorId: string) {
  return useQuery({
    queryKey: ['tutor-dashboard-stats', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<TutorDashboardStats> => {
      const [{ data: students }, { data: classes }, { data: exercises }] = await Promise.all([
        supabase
          .from('tutor_links')
          .select('id, status, student_id')
          .eq('tutor_id', tutorId)
          .eq('status', 'active'),
        supabase.from('classes').select('id').eq('tutor_id', tutorId).is('archived_at', null),
        supabase.from('exercises').select('id').eq('created_by', tutorId),
      ]);

      const activeStudentIds = (students ?? []).map((s) => s.student_id);

      let studentsAtRisk = 0;
      if (activeStudentIds.length > 0) {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const { data: sp } = await supabase
          .from('student_profiles')
          .select('id, last_activity_at')
          .in('id', activeStudentIds);
        studentsAtRisk = (sp ?? []).filter(
          (s) => !s.last_activity_at || s.last_activity_at < sevenDaysAgo,
        ).length;
      }

      return {
        total_students: students?.length ?? 0,
        active_classes: classes?.length ?? 0,
        total_exercises: exercises?.length ?? 0,
        students_at_risk: studentsAtRisk,
      };
    },
  });
}

export function useRecentActivity(tutorId: string) {
  return useQuery({
    queryKey: ['tutor-recent-activity', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<RecentActivity[]> => {
      const { data: links } = await supabase
        .from('tutor_links')
        .select('student_id')
        .eq('tutor_id', tutorId)
        .eq('status', 'active');

      const studentIds = (links ?? []).map((l) => l.student_id);
      if (studentIds.length === 0) return [];

      const { data: attempts } = await supabase
        .from('attempts')
        .select(
          'id, student_id, status, started_at, exercises(title), profiles!attempts_student_id_fkey(display_name:visible_name)',
        )
        .in('student_id', studentIds)
        .order('started_at', { ascending: false })
        .limit(15);

      return (attempts ?? []).map((a: any) => ({
        attempt_id: a.id,
        student_name: (a.profiles as any)?.display_name ?? '(élève)',
        exercise_title: a.exercises?.title ?? '(exercice supprimé)',
        status: a.status,
        started_at: a.started_at,
      }));
    },
  });
}
